// Cálculos puros del modal de confirmación al CONTRATAR extras
// (Usuario > Suscripción > tab Extras). Sin llamadas a Stripe: usa los
// mismos datos y la misma fórmula que ya pinta la pestaña (precio BD sin
// IVA × 1.21 vía `resolveFinalPrice`, ver `src/utils/pricing.js`).

import { resolveFinalPrice } from "src/utils/pricing";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Extras de "unidades": se contratan N (dispositivos, mesas, packs WhatsApp).
export const isCountableAddOn = (addOn) =>
  ["device", "tables", "whatsapp_pack"].includes(addOn?.kind);

// Periodo de cobro del extra según su ciclo.
export const periodLabelForAddOn = (addOn) =>
  addOn?.billingCycle === "yearly" ? "año" : "mes";

const cycleOf = (addOn) => (addOn?.billingCycle === "yearly" ? "yearly" : "monthly");

/**
 * Devuelve el add-on del ciclo del plan: si el pedido es del otro ciclo y
 * tiene gemelo (`siblingAddOnId`) en el catálogo, el gemelo. Es lo mismo
 * que hace el backend (en un plan anual se contrata y cobra el anual).
 */
export function resolveAddOnForCycle(addOn, addOnsById, planCycle) {
  if (!addOn || !planCycle || cycleOf(addOn) === planCycle) return addOn;
  const sibling = addOn.siblingAddOnId ? addOnsById?.[addOn.siblingAddOnId] : null;
  return sibling && cycleOf(sibling) === planCycle ? sibling : addOn;
}

/** Por nombre (modal global "Contrátalo"): el del ciclo del plan si existe. */
export function pickAddOnForCycle(addOns, name, planCycle) {
  const wanted = (name || "").trim().toLowerCase();
  const matches = (addOns || []).filter(
    (a) => (a.name || "").trim().toLowerCase() === wanted && a.isActive !== false,
  );
  if (matches.length === 0) return null;
  return matches.find((a) => cycleOf(a) === planCycle) || matches[0];
}

/** "12,10€ / mes" o "121,00€ / año" con IVA incluido. */
export const addOnPriceLabel = (addOn) =>
  `${round2(resolveFinalPrice(addOn)).toFixed(2)}€ / ${periodLabelForAddOn(addOn)}`;

/**
 * Líneas del modal a partir de los cambios pedidos.
 * @param {Array<{addOnId:number,currentQuantity:number,targetQuantity:number}>} items
 * @param {Object<number, object>} addOnsById catálogo por id
 * @param {"monthly"|"yearly"} [planCycle] ciclo del plan del usuario
 */
export function buildContractLines(items, addOnsById, planCycle) {
  return (items || [])
    .map((it) => {
      const addOn = resolveAddOnForCycle(addOnsById?.[it.addOnId], addOnsById, planCycle);
      const currentQuantity = Math.max(0, Number(it.currentQuantity) || 0);
      const targetQuantity = Math.max(0, Number(it.targetQuantity) || 0);
      const delta = Math.max(0, targetQuantity - currentQuantity);
      const unitPrice = resolveFinalPrice(addOn);
      return {
        addOnId: it.addOnId,
        name: addOn?.name || `Extra #${it.addOnId}`,
        currentQuantity,
        targetQuantity,
        delta,
        unitPrice,
        lineExtra: round2(unitPrice * delta),
        periodLabel: periodLabelForAddOn(addOn),
        isCountable: isCountableAddOn(addOn),
      };
    })
    .filter((l) => l.delta > 0);
}

/** Sobrecoste por periodo (IVA incl.) de todas las líneas. */
export const contractExtraTotal = (lines) =>
  round2((lines || []).reduce((s, l) => s + (l.lineExtra || 0), 0));

/** Nueva cuota = cuota actual (la del aside "Próximo recibo") + sobrecoste. */
export const contractNewTotal = (currentTotal, extra) =>
  round2((Number(currentTotal) || 0) + (Number(extra) || 0));

/** Texto de unidades "1 → 3" solo para extras de unidades. */
export const unitsLabel = (line) =>
  line?.isCountable
    ? `${line.currentQuantity} → ${line.targetQuantity} ${line.targetQuantity === 1 ? "unidad" : "unidades"}`
    : null;

/** Fecha de próxima renovación que ya usa la página (aside + modal de baja). */
export function resolveRenewalDate(user, upcomingInvoice) {
  const raw =
    user?.finishDemoTrial ||
    upcomingInvoice?.nextPaymentAttempt ||
    upcomingInvoice?.periodEnd ||
    null;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** DD/MM/AAAA */
export function formatDateDMY(date) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/** Línea informativa del prorrateo; null si no hay fecha. */
export function prorationNote(renewalDate) {
  const f = formatDateDMY(renewalDate);
  if (!f) return null;
  return `Hoy se cobrará la parte proporcional hasta tu próxima renovación (${f}); a partir de entonces, la cuota completa.`;
}

export const formatEuro = (n) => `${round2(n).toFixed(2)}€`;
